import type { SolverRequest, SolverResult, UserPlan, Workbench } from '@minglun/domain';

export interface SolverHistoryMetadata { id: string; title: string; createdAt: string; studyId?: string; studyName?: string }

export function createSolverRequest(workbench: Workbench, userPlan: UserPlan): SolverRequest {
  return { workbench: structuredClone(workbench), userPlan: structuredClone(userPlan) };
}

export function mergeSolverResult(current: UserPlan, request: SolverRequest, result: SolverResult, metadata: SolverHistoryMetadata): UserPlan {
  if (result.status === 'cancelled' || result.status === 'failed' || result.status === 'not_run') return current;
  const entry = {
    ...metadata,
    workbenchVersion: request.workbench.version,
    weightFocusExponent: request.workbench.globalSettings.weightFocusExponent,
    scorePrecisionDigits: request.workbench.globalSettings.scorePrecisionDigits,
    configuration: structuredClone(request.userPlan.solver),
    objectives: structuredClone(request.userPlan.objectives),
    baseAttributes: structuredClone(request.userPlan.baseAttributes),
    inventory: structuredClone(request.userPlan.inventory),
    result: structuredClone(result)
  };
  // The user can edit another study while the worker is running. Archive the
  // result against the study that submitted it, without changing that editor.
  const differentStudy = metadata.studyId && current.activeStudyId !== metadata.studyId;
  return {
    ...current,
    ...(!differentStudy ? { lastResult: entry.result } : {}),
    ...(differentStudy && current.studies ? { studies: current.studies.map(study => study.id === metadata.studyId ? { ...study, lastResult: entry.result } : study) } : {}),
    solverHistory: [entry, ...current.solverHistory],
  };
}

export function updateWheelExecutionState(plan: UserPlan, historyId: string, wheelIds: string[], isApplied: boolean): UserPlan {
  const ids = new Set(wheelIds); if (!ids.size) return plan;
  const updateResult = (value: SolverResult): SolverResult => ({ ...value, fateWheels: value.fateWheels.map((item) => ids.has(item.fateWheelId) ? { ...item, isApplied } : item) });
  const selectedCompletedAt = historyId === 'current' ? plan.lastResult?.completedAt : plan.solverHistory.find((entry) => entry.id === historyId)?.result.completedAt;
  if (!selectedCompletedAt) return plan;
  return {
    ...plan,
    ...(plan.lastResult?.completedAt === selectedCompletedAt ? { lastResult: updateResult(plan.lastResult) } : {}),
    solverHistory: plan.solverHistory.map((entry) => entry.id === historyId || entry.result.completedAt === selectedCompletedAt ? { ...entry, result: updateResult(entry.result) } : entry)
  };
}

export function updateComparisonExecutionState(
  plan: UserPlan,
  comparisonHistoryId: string,
  baselineCompletedAt: string,
  wheelIds: string[],
  isApplied: boolean,
): UserPlan {
  const ids = new Set(wheelIds);
  if (!ids.size || !baselineCompletedAt) return plan;
  return {
    ...plan,
    solverHistory: plan.solverHistory.map((entry) => {
      if (entry.id !== comparisonHistoryId) return entry;
      const executions = entry.comparisonExecutions ?? [];
      const applied = new Set(
        executions.find((item) => item.baselineCompletedAt === baselineCompletedAt)
          ?.appliedFateWheelIds ?? [],
      );
      for (const wheelId of ids) {
        if (isApplied) applied.add(wheelId);
        else applied.delete(wheelId);
      }
      const next = executions.filter(
        (item) => item.baselineCompletedAt !== baselineCompletedAt,
      );
      if (applied.size) {
        next.push({
          baselineCompletedAt,
          appliedFateWheelIds: [...applied],
        });
      }
      if (next.length) return { ...entry, comparisonExecutions: next };
      const { comparisonExecutions: _removed, ...withoutExecutions } = entry;
      return withoutExecutions;
    }),
  };
}
